import { Link, useLocation, useNavigate } from "react-router";
import { Menu, X, ShoppingBag, Coins, User, LogOut, Gift } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { routes } from "../routes";
import { useCartStore } from "../store/cartStore";
import { useAuthStore } from "../store/authStore";
import { ReferralModal } from "./ReferralModal";
import { PricingModal } from "./PricingModal";

export function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const itemCount = useCartStore((s) => s.itemCount());
  const openDrawer = useCartStore((s) => s.openDrawer);

  const tokens = useAuthStore((s) => s.tokens);
  const user = useAuthStore((s) => s.user);
  const wallet = useAuthStore((s) => s.wallet);
  const logout = useAuthStore((s) => s.logout);

  const isLoggedIn = !!tokens?.access?.token;

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [userMenuOpen]);

  function handleLogout() {
    logout();
    setUserMenuOpen(false);
    setMenuOpen(false);
    // Stay on the current page after logout
    navigate(location.pathname + location.search, { replace: true });
  }

  const navLinks = [
    { label: "Home", to: routes.home },
    { label: "Shop", to: routes.shop },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/95 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-6">
        {/* Logo */}
        <Link to={routes.home} className="flex shrink-0 items-center gap-2">
          <img
            src="/images/logo-colored.svg"
            alt="Ghibgen"
            width={44}
            height={44}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium transition-colors hover:text-primary-400 ${
                location.pathname === link.to
                  ? "text-primary-400"
                  : "text-night-ink"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-4">
          {/* Cart icon */}
          <button
            type="button"
            onClick={openDrawer}
            className="relative text-primary-400 transition-colors hover:text-primary-500"
            aria-label="Open cart"
          >
            <ShoppingBag className="h-5 w-5" />
            {itemCount > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary-400 text-[10px] font-bold text-white">
                {itemCount}
              </span>
            )}
          </button>

          {isLoggedIn ? (
            <>
              {/* Credits */}
              <button
                type="button"
                onClick={() => setPricingOpen(true)}
                className="flex items-center gap-1.5 text-sm font-medium text-primary-400 hover:text-primary-500"
                aria-label="Credits"
              >
                <Coins className="h-4 w-4" />
                <span>{wallet?.credits ?? 0}</span>
              </button>

              {/* User dropdown */}
              <div className="relative hidden md:block" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-400/10 text-primary-400 transition-colors hover:bg-primary-400/20"
                  aria-label="User menu"
                >
                  <User className="h-4 w-4" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-slate-100 bg-white shadow-lg">
                    <div className="border-b border-slate-100 px-4 py-3">
                      <p className="truncate text-sm font-medium text-night-ink">
                        {user?.email}
                      </p>
                    </div>
                    <div className="py-1">
                      <Link
                        to={routes.myCreations}
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-night-ink hover:bg-slate-50"
                      >
                        Past creations
                      </Link>
                      <button
                        type="button"
                        onClick={() => { setUserMenuOpen(false); setReferralOpen(true); }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-night-ink hover:bg-slate-50"
                      >
                        <Gift className="h-4 w-4" /> Invite a friend
                      </button>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-slate-50"
                      >
                        <LogOut className="h-4 w-4" /> Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link
              to={`${routes.login}?next=${encodeURIComponent(location.pathname + location.search)}`}
              className="text-sm font-medium text-primary-400 hover:text-primary-500"
            >
              Login
            </Link>
          )}

          {/* Mobile toggle */}
          <button
            type="button"
            className="text-primary-400 transition-transform duration-300 md:hidden"
            style={{ transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      <nav
        className={`absolute left-0 right-0 top-full z-40 origin-top border-t border-slate-100 bg-white px-6 py-4 shadow-lg transition-all duration-200 ease-out md:hidden ${
          menuOpen
            ? "pointer-events-auto scale-y-100 opacity-100"
            : "pointer-events-none scale-y-0 opacity-0"
        }`}
      >
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              className={`block py-2 text-sm font-medium transition-colors hover:text-primary-400 ${
                location.pathname === link.to
                  ? "text-primary-400"
                  : "text-night-ink"
              }`}
            >
              {link.label}
            </Link>
          ))}

          {isLoggedIn ? (
            <>
              <div className="my-2 border-t border-slate-100" />
              <p className="truncate py-2 text-xs text-slate-500">
                {user?.email}
              </p>
              <Link
                to={routes.myCreations}
                onClick={() => setMenuOpen(false)}
                className="block py-2 text-sm font-medium text-night-ink hover:text-primary-400"
              >
                Past creations
              </Link>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); setReferralOpen(true); }}
                className="flex items-center gap-1.5 py-2 text-sm font-medium text-night-ink hover:text-primary-400"
              >
                <Gift className="h-4 w-4" /> Invite a friend
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center gap-1.5 py-2 text-sm font-medium text-red-600"
              >
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </>
          ) : (
            <>
              <div className="my-2 border-t border-slate-100" />
              <Link
                to={`${routes.login}?next=${encodeURIComponent(location.pathname + location.search)}`}
                onClick={() => setMenuOpen(false)}
                className="block py-2 text-sm font-medium text-primary-400"
              >
                Login
              </Link>
              <Link
                to={`${routes.signUp}?next=${encodeURIComponent(location.pathname + location.search)}`}
                onClick={() => setMenuOpen(false)}
                className="block py-2 text-sm font-medium text-primary-400"
              >
                Sign up
              </Link>
            </>
          )}
        </nav>
      {createPortal(
        <ReferralModal open={referralOpen} onClose={() => setReferralOpen(false)} />,
        document.body
      )}
      {pricingOpen && createPortal(
        <PricingModal onClose={() => setPricingOpen(false)} />,
        document.body
      )}
    </header>
  );
}
